import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { useConfirm } from "../../components/Dialog";
import { useAuth } from "../../context/AuthContext";
import {
  ADMIN_CANCELED_STATUS,
  CANCELLATION_KIND_LABELS,
  CANCELLATION_REQUEST_STATUSES,
  CANCELLATION_REASON_LABELS,
  CANCELLATION_STATUS_LABELS,
  CancellationFilterStatus,
  CancellationRequestDto,
  CancellationRequestListResponse,
} from "../../types/cancellation";
import Pagination from "../OnboardingCustomers/components/Pagination";
import CancellationRequestActions from "./components/CancellationRequestActions";

const DEFAULT_PAGE_SIZE = 25;

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const STATUS_STYLES: Record<CancellationFilterStatus, React.CSSProperties> = {
  DRAFT: { background: "#fff8e1", color: "#856404", borderColor: "#ffe69c" },
  SIGNATURE_PENDING: {
    background: "#e3f2fd",
    color: "#0d47a1",
    borderColor: "#bbdefb",
  },
  SIGNED: { background: "#e0f2f1", color: "#00695c", borderColor: "#b2dfdb" },
  RECESSO_PENDING: {
    background: "#fff3e0",
    color: "#a15c00",
    borderColor: "#ffe0b2",
  },
  COMPLETED: { background: "#d4edda", color: "#155724", borderColor: "#c3e6cb" },
  REJECTED: { background: "#f8d7da", color: "#721c24", borderColor: "#f5c6cb" },
  // Synthetic "Cancelado" pill for Tipo 2/3 administrative cancellations
  CANCELED: { background: "#f1f3f5", color: "#495057", borderColor: "#dee2e6" },
};

const FILTER_STATUSES: CancellationFilterStatus[] = [
  ...CANCELLATION_REQUEST_STATUSES,
  ADMIN_CANCELED_STATUS,
];

const FILTER_STATUS_LABELS: Record<CancellationFilterStatus, string> = {
  ...CANCELLATION_STATUS_LABELS,
  CANCELED: "Cancelado (Tipo 2/3)",
};

export default function CancellationRequests() {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [data, setData] = useState<CancellationRequestListResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [statuses, setStatuses] = useState<CancellationFilterStatus[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (statuses.length > 0) {
        params.set("status", statuses.join(","));
      }
      const response = await request<CancellationRequestListResponse>(
        `/api/admin/operations/cancellation-requests?${params.toString()}`,
        { token },
      );
      setData(response);
    } catch (err) {
      setError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar",
      );
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, statuses]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = (status: CancellationFilterStatus) => {
    setPage(1);
    setStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  };

  const total = data?.total ?? 0;

  return (
    <div style={{ padding: "1.5rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "1.1rem", color: "#2A3F54" }}>
          Cancelaciones
        </h1>
        <span style={{ color: "#73879C", fontSize: ".82rem" }}>
          {loading ? "Cargando…" : `${total} registros`}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.4rem",
          marginBottom: "1rem",
        }}
      >
        {FILTER_STATUSES.map((s) => {
          const active = statuses.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              style={{
                ...statusChipStyle,
                ...(active
                  ? { ...STATUS_STYLES[s], border: `1px solid ${STATUS_STYLES[s].borderColor}` }
                  : {}),
              }}
            >
              {FILTER_STATUS_LABELS[s]}
            </button>
          );
        })}
      </div>

      {error && (
        <div
          style={{
            background: "#fde2e2",
            color: "#b32424",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            marginBottom: "1rem",
            fontSize: ".85rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: ".85rem",
          }}
        >
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              <Th>Tipo</Th>
              <Th>Estado</Th>
              <Th>Tomador</Th>
              <Th>Póliza</Th>
              <Th>Motivo</Th>
              <Th>Solicitud</Th>
              <Th>Firmada</Th>
              <Th>Fecha efectiva</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {!loading && data && data.items.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  style={{
                    padding: "1.5rem",
                    textAlign: "center",
                    color: "#73879C",
                  }}
                >
                  Sin resultados.
                </td>
              </tr>
            )}
            {data?.items.map((row) => (
              <CancellationRow
                key={row.id}
                row={row}
                confirm={confirm}
                token={token}
                onReload={load}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: "1rem" }}>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPage(1);
            setPageSize(size);
          }}
          disabled={loading}
        />
      </div>
    </div>
  );
}

function CancellationRow({
  row,
  token,
  confirm,
  onReload,
}: {
  row: CancellationRequestDto;
  token: string | null;
  confirm: ReturnType<typeof useConfirm>;
  onReload: () => void;
}) {
  const isAdmin = row.kind === "TIPO_2_3";
  const motivo = isAdmin
    ? [row.cancellationType, row.reasonLabel].filter(Boolean).join(" — ")
    : (row.reason
        ? CANCELLATION_REASON_LABELS[row.reason]
        : row.reasonLabel);
  return (
    <tr style={{ borderTop: "1px solid #f1f3f5" }}>
      <Td>
        <span
          style={{
            display: "inline-block",
            padding: "0.15rem 0.5rem",
            borderRadius: "0.4rem",
            fontSize: ".7rem",
            fontWeight: 600,
            border: "1px solid",
            ...(isAdmin
              ? { background: "#fff3cd", color: "#856404", borderColor: "#ffe69c" }
              : { background: "#e7f1ff", color: "#0a58ca", borderColor: "#cfe2ff" }),
          }}
        >
          {CANCELLATION_KIND_LABELS[row.kind]}
        </span>
      </Td>
      <Td>
        <span
          style={{
            display: "inline-block",
            padding: "0.15rem 0.5rem",
            borderRadius: "0.4rem",
            fontSize: ".72rem",
            fontWeight: 600,
            border: "1px solid",
            ...STATUS_STYLES[row.status],
          }}
        >
          {FILTER_STATUS_LABELS[row.status]}
        </span>
      </Td>
      <Td>
        <Link
          to={`/leads/${row.leadUlid}`}
          style={{ color: "#0069d9", fontWeight: 600 }}
        >
          {row.tomadorFirstName} {row.tomadorLastName}
        </Link>
      </Td>
      <Td>{row.policyNumber || "—"}</Td>
      <Td>
        <span
          title={motivo || undefined}
          style={{
            display: "inline-block",
            maxWidth: "14rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            verticalAlign: "bottom",
          }}
        >
          {motivo || "—"}
        </span>
      </Td>
      <Td>{fmtDate(row.requestDate)}</Td>
      <Td>{fmtDate(row.signedAt)}</Td>
      <Td>{fmtDate(row.effectiveCancellationDate)}</Td>
      <Td>
        {isAdmin ? (
          <span style={{ color: "#73879C", fontSize: ".75rem" }}>—</span>
        ) : (
          <CancellationRequestActions
            row={row}
            token={token}
            confirm={confirm}
            onReload={onReload}
          />
        )}
      </Td>
    </tr>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: "0.65rem 0.8rem",
        textAlign: "left",
        fontWeight: 600,
        color: "#1f2933",
        fontSize: ".75rem",
        textTransform: "uppercase",
        letterSpacing: ".3px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td
      style={{
        padding: "0.6rem 0.8rem",
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </td>
  );
}

const statusChipStyle: React.CSSProperties = {
  padding: "0.3rem 0.7rem",
  borderRadius: "0.4rem",
  fontSize: ".78rem",
  fontWeight: 600,
  border: "1px solid #dee2e6",
  background: "white",
  color: "#73879C",
  cursor: "pointer",
};
